import type {ProjectsTree, TreeFilter} from '../selectors/projectTrees'
import type {BuildType} from '../services/rest'
import type {
  BuildTypeId,
  FederationServerId,
  NodeGroup,
  NormalizedProject,
  ProjectId,
  Template,
  ExpandState,
} from '../types'
import {ROOT_PROJECT_ID} from '../types'
import {base_uri} from '../types/BS_types'
import type {ProjectsTreeItem} from '../types/projectTrees'

import type {KeyValue, WritableMap} from './object'

const currentServerId: FederationServerId = base_uri

type ProcessProjectProps = {
  project: NormalizedProject
  parentId?: ProjectId
  childrenByProject: WritableMap<ProjectId, ReadonlyArray<NormalizedProject>>
  level: number
  treeData: Array<ProjectsTreeItem>
  group: NodeGroup
  serverId?: FederationServerId
  removeBuildTypes: boolean
  disabledProjects?: ReadonlyArray<ProjectId> | null | undefined
  isIncluded?: boolean
}

type ProcessChildrenProps = {
  child: NormalizedProject
  childId: string
  parentId: string
  childrenByProject: WritableMap<ProjectId, ReadonlyArray<NormalizedProject>>
  level: number
  treeData: Array<ProjectsTreeItem>
  buildTypes: KeyValue<BuildTypeId, BuildType>
  group: NodeGroup
  serverId?: FederationServerId
  removeBuildTypes: boolean
  includedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  excludedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  disabledProjects?: ReadonlyArray<ProjectId> | null | undefined
  includedParents: Set<ProjectId>
  templates: KeyValue<any, readonly Template[]>
  showArchivedProjects?: boolean
  isIncluded?: boolean
}

type ProcessBuildTypesProps = {
  project: NormalizedProject | null | undefined
  level: number
  buildTypes: KeyValue<BuildTypeId, BuildType>
  templates?: readonly Template[]
  treeData: ProjectsTreeItem[]
  group: NodeGroup
  serverId?: FederationServerId
  includedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  excludedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
}

type ConvertDataToTreeProps = {
  ready: boolean
  data: ReadonlyArray<ProjectId>
  buildTypes: KeyValue<BuildTypeId, BuildType>
  projects: KeyValue<ProjectId, NormalizedProject>
  templates?: KeyValue<ProjectId, readonly Template[]>
  group: NodeGroup
  allProjects?: KeyValue<ProjectId, NormalizedProject>
  serverId?: FederationServerId
  filter?: TreeFilter
  onlyIncluded?: boolean
  showArchivedProjects?: boolean
}

type GetValidParentsProps = {
  data: ReadonlyArray<ProjectId>
  allProjects: KeyValue<ProjectId, NormalizedProject>
  onlyIncluded?: boolean
}

type GetChildrenByProjectProps = {
  data: ReadonlyArray<ProjectId>
  validParents: WritableMap<ProjectId, NormalizedProject | undefined>
  projects: KeyValue<ProjectId, NormalizedProject>
}

export function getValidParents({data, allProjects, onlyIncluded}: GetValidParentsProps) {
  const validParents: WritableMap<ProjectId, NormalizedProject | undefined> = new Map()
  const hashMap = new Map<ProjectId, boolean>(data.map(projectId => [projectId, true]))
  const addedParents = new Set<ProjectId>()

  data.forEach((projectId: ProjectId) => {
    const currentProject = allProjects[projectId]

    if (currentProject != null) {
      let currentId = projectId
      let parentId = currentProject.parentProjectId

      if (onlyIncluded) {
        while (parentId && parentId !== ROOT_PROJECT_ID && !hashMap.has(parentId)) {
          parentId = allProjects[parentId]?.parentProjectId
        }
      }

      validParents.set(projectId, allProjects[parentId ?? ROOT_PROJECT_ID])

      if (!onlyIncluded) {
        while (
          parentId &&
          parentId !== ROOT_PROJECT_ID &&
          (!hashMap.has(currentId) || !hashMap.has(parentId))
        ) {
          const project = allProjects[parentId]
          if (project != null) {
            if (currentId !== projectId) {
              validParents.set(currentId, project)
            }
            if (!hashMap.has(parentId)) {
              addedParents.add(parentId)
            }
          }
          currentId = parentId
          parentId = project?.parentProjectId
        }
      }
    }
  })

  return {validParents, addedParents: [...addedParents]}
}

export function getChildrenByProject({data, validParents, projects}: GetChildrenByProjectProps) {
  const childrenByProject: WritableMap<ProjectId, Array<NormalizedProject>> = new Map()

  data.forEach((projectId: ProjectId) => {
    const project = projects[projectId]

    if (project) {
      if (!childrenByProject.has(projectId)) {
        childrenByProject.set(projectId, [])
      }

      if (projectId === ROOT_PROJECT_ID) {
        return
      }

      const validParent = validParents.get(project.id)
      const parentProjectId = validParent ? validParent.id : ROOT_PROJECT_ID
      const siblings = childrenByProject.get(parentProjectId)

      if (siblings) {
        siblings.push(project)
      } else {
        childrenByProject.set(parentProjectId, [project])
      }
    }
  })

  return childrenByProject
}

function processProject({
  project,
  parentId,
  childrenByProject,
  level,
  treeData,
  group,
  serverId = currentServerId,
  removeBuildTypes,
  disabledProjects,
  isIncluded,
}: ProcessProjectProps) {
  const projectId = project.id ?? ''
  const buildTypes = removeBuildTypes ? [] : project.buildTypes?.buildType ?? []
  const children = childrenByProject.get(projectId) ?? []
  const hasChildren = buildTypes.length > 0 || children.length > 0
  const itemType = 'project'
  const key = `${serverId}_${itemType}_${projectId}`

  treeData.push({
    itemType,
    id: projectId,
    internalId: project.internalId,
    name: project.name || '',
    level,
    parentId,
    actualParentId: project.parentProjectId,
    hasChildren,
    archived: project.archived,
    childrenProjectsIds: children.map(child => child.id),
    childrenBuildTypesIds: buildTypes,
    searchMatches: null,
    expanded: true,
    group,
    serverId,
    focusable: true,
    isDisabled: !isIncluded || disabledProjects?.includes(projectId),
    key,
  })
}

function processChildren({
  child,
  childId,
  parentId,
  childrenByProject,
  level,
  treeData,
  buildTypes,
  group,
  serverId = currentServerId,
  removeBuildTypes,
  includedBuildTypes,
  excludedBuildTypes,
  disabledProjects,
  includedParents,
  templates,
  showArchivedProjects,
  isIncluded,
}: ProcessChildrenProps) {
  if (!isIncluded && !includedParents.has(childId)) {
    return
  }

  processProject({
    project: child,
    parentId,
    childrenByProject,
    level,
    treeData,
    group,
    serverId,
    removeBuildTypes,
    disabledProjects,
    isIncluded,
  })

  if (!removeBuildTypes && isIncluded && (!child.archived || showArchivedProjects)) {
    processBuildTypes({
      project: child,
      level: level + 1,
      treeData,
      buildTypes,
      templates: templates[childId],
      group,
      serverId,
      includedBuildTypes,
      excludedBuildTypes,
    })
  }
}

function processBuildTypes({
  project,
  level,
  buildTypes,
  templates = [],
  treeData,
  group,
  serverId = currentServerId,
  includedBuildTypes,
  excludedBuildTypes,
}: ProcessBuildTypesProps) {
  if (project == null) {
    return
  }

  let types: ReadonlyArray<BuildTypeId> = project.buildTypes?.buildType ?? []

  if (includedBuildTypes != null) {
    types = types.filter(id => includedBuildTypes.includes(id))
  }

  if (excludedBuildTypes != null) {
    types = types.filter(id => !excludedBuildTypes.includes(id))
  }

  types.forEach((buildTypeId: BuildTypeId) => {
    const buildType = buildTypes[buildTypeId]

    if (buildType != null) {
      const {id = '', name = '', type, internalId} = buildType
      const itemType = 'buildType'
      const key = `${serverId}_${itemType}_${id}`
      const item: ProjectsTreeItem = {
        itemType,
        internalId,
        id,
        name,
        level,
        parentId: project.id,
        archived: project.archived,
        type,
        searchMatches: null,
        group,
        serverId,
        focusable: true,
        key,
      }

      if (group === 'favorites') {
        item.paused = buildType.paused
      }

      treeData.push(item)
    }
  })

  templates.forEach(({id = '', name}) => {
    const itemType = 'template'
    const key = `${serverId}_${itemType}_${id}`

    treeData.push({
      itemType,
      id,
      name,
      level,
      parentId: project.id,
      archived: project.archived,
      searchMatches: null,
      group,
      serverId,
      focusable: true,
      key,
    })
  })
}

/**
 * Info: Performance improvement
 * This method is used in different places with big data. Please be careful to editing it
 */
export function convertDataToTree({
  ready,
  data,
  buildTypes,
  projects,
  templates = {},
  group,
  allProjects = projects,
  serverId,
  filter = {},
  onlyIncluded,
  showArchivedProjects,
}: ConvertDataToTreeProps): ProjectsTree | null | undefined {
  if (!ready) {
    return null
  }

  const treeData: Array<ProjectsTreeItem> = []
  const {
    parentId = ROOT_PROJECT_ID,
    removeBuildTypes = false,
    removeEmptyProjects = false,
    includedBuildTypes,
    excludedBuildTypes,
    includedProjects,
    includedRootProjects,
    disabledProjects,
    includeRoot = false,
  } = filter
  const includedProjectsSet = new Set(includedProjects)
  const includedRootProjectsSet = new Set(includedRootProjects)
  const getIsIncludedProjects = (id: string) =>
    includedProjects == null || includedProjectsSet.has(id)
  const getIsIncludedRootProjects = (id: string) =>
    includedRootProjects == null || includedRootProjectsSet.has(id)
  const baseLevel = group === 'all' || includeRoot ? 1 : 0
  let filteredData = data

  if (removeEmptyProjects || includedBuildTypes != null) {
    const nonEmpty = new Set()

    const markAsNonEmptyRecursively = (id: ProjectId | null | undefined) => {
      if (id != null && !nonEmpty.has(id)) {
        nonEmpty.add(id)
        markAsNonEmptyRecursively(projects[id]?.parentProjectId)
      }
    }

    data.forEach(projectId => {
      const project = projects[projectId]
      const childBuildTypes = project?.buildTypes?.buildType ?? []
      const childTemplates = templates[projectId] ?? []

      if (
        (childBuildTypes.length > 0 || childTemplates.length > 0) &&
        (includedBuildTypes == null ||
          includedBuildTypes.some(id => childBuildTypes.includes(id))) &&
        (excludedBuildTypes == null || childBuildTypes.some(id => !excludedBuildTypes.includes(id)))
      ) {
        markAsNonEmptyRecursively(project?.id)
      }
    })
    filteredData = data.filter(id => nonEmpty.has(id))
  }

  const includedParents = new Set<string>()

  const includeParentsRecursively = (id: ProjectId) => {
    const parentProjectId = projects[id]?.parentProjectId

    if (parentProjectId != null && !includedParents.has(parentProjectId)) {
      includedParents.add(parentProjectId)
      includeParentsRecursively(parentProjectId)
    }
  }

  includedProjects?.forEach(includeParentsRecursively)
  includedRootProjects?.forEach(includeParentsRecursively)

  const {validParents, addedParents} = getValidParents({
    data: filteredData,
    allProjects,
    onlyIncluded,
  })

  const childrenByProject = getChildrenByProject({
    data: filteredData.concat(addedParents),
    validParents,
    projects,
  })

  const parent = projects[parentId]

  if (includeRoot && parent != null) {
    const isIncluded = getIsIncludedProjects(parent.id) && getIsIncludedRootProjects(parent.id)

    processProject({
      project: parent,
      childrenByProject,
      level: 0,
      treeData,
      group,
      serverId,
      removeBuildTypes,
      disabledProjects,
      isIncluded,
    })
  }

  if (!removeBuildTypes && getIsIncludedProjects(parentId)) {
    processBuildTypes({
      project: group === 'favorites' ? projects[parentId] : allProjects[parentId],
      level: baseLevel,
      treeData,
      buildTypes,
      templates: templates[parentId],
      group,
      serverId,
      includedBuildTypes,
      excludedBuildTypes,
    })
  }

  const rootItems = childrenByProject.get(parentId)
  const rootLength = rootItems?.length

  if (rootLength) {
    const parentIds = [parentId, parentId]
    let level = baseLevel
    let includedLevel = level
    let stack: Array<NormalizedProject | null> = [...rootItems]
    const processChildrenArgumens: ProcessChildrenProps = {
      child: parent!,
      parentId,
      childId: parentId,
      childrenByProject,
      treeData,
      buildTypes,
      group,
      serverId,
      level,
      removeBuildTypes,
      includedBuildTypes,
      excludedBuildTypes,
      disabledProjects,
      includedParents,
      templates,
      showArchivedProjects,
    }

    while (stack.length) {
      const parentChild = stack.shift()

      if (parentChild != null) {
        const {id} = parentChild
        const currentParentId = parentIds[level]
        const parentChilds: Array<NormalizedProject> | undefined = childrenByProject.get(id)
        const hasChild = parentChilds?.length
        const currentLevelHigher = level <= includedLevel
        const isIncludedProject = getIsIncludedProjects(id)
        const isIncludedRootProject = currentLevelHigher ? getIsIncludedRootProjects(id) : true
        const isIncluded = isIncludedProject && isIncludedRootProject

        processChildrenArgumens.child = parentChild
        processChildrenArgumens.childId = id
        processChildrenArgumens.level = level
        processChildrenArgumens.parentId = currentParentId
        processChildrenArgumens.isIncluded = isIncluded

        processChildren(processChildrenArgumens)

        if (hasChild) {
          level++
          parentIds[level] = id
          stack = [...parentChilds, null, ...stack]
        }

        if (currentLevelHigher && !isIncluded) {
          includedLevel = level
        }
      } else {
        level--
      }
    }
  }

  return {
    data: treeData,
    _hash: undefined,
    /**
     * Info: Performance improvement
     * Creating a deferred hashmap on demand by the object getter
     */
    get hash() {
      if (!this._hash) {
        this._hash = new Map(
          this.data.map((item, index) => {
            item.index = index
            return [item.key!, item]
          }),
        )
      }

      return this._hash
    },
  }
}

export const upgradeTreeByExpandState = ({
  defaultTree,
  expandState,
  baseLevel,
  shift,
  showArchivedProjects,
}: {
  defaultTree: ProjectsTree | null | undefined
  expandState: KeyValue<ProjectId, ExpandState> | null | undefined
  baseLevel: number
  shift?: number
  showArchivedProjects?: boolean
}): ProjectsTree => {
  const treeData: Array<ProjectsTreeItem> = []

  if (defaultTree?.data) {
    const localExpandState: WritableMap<ProjectId, boolean> = new Map()
    defaultTree.data.forEach((item: ProjectsTreeItem) => {
      const {parentId, itemType, id} = item
      const newItem = {...item}

      if (item.level != null && shift != null) {
        newItem.level += shift
      }

      if (itemType === 'project') {
        if (parentId && localExpandState.get(parentId) === false) {
          localExpandState.set(item.id, false)
          newItem.expanded = false
        } else {
          const defaultExpanded = !showArchivedProjects && item.archived ? 'EXPANDED' : 'COLLAPSED'
          const expandStateWithDefault = expandState?.[id]?.expandState ?? defaultExpanded
          const expanded = expandStateWithDefault === 'EXPANDED'
          localExpandState.set(id, expanded)
          newItem.expanded = expanded
        }
      }

      if (newItem.level === baseLevel || (parentId && localExpandState.get(parentId))) {
        treeData.push(newItem)
      }
    })
  }

  return {
    data: treeData,
    _hash: undefined,
    /**
     * Info: Performance improvement
     * Creating a deferred hashmap on demand by the object getter
     */
    get hash() {
      if (!this._hash) {
        this._hash = new Map(
          treeData.map(item => [`${item.serverId}_${item.itemType}_${item.id}`, item]),
        )
      }
      return this._hash
    },
  }
}
